import { Type } from 'typebox';
import { ApiRequestError, ApiResponseParseError } from '../core/api-error.js';
const AttachmentDownloadUrlPayload = Type.Object({
    url: Type.String({ pattern: '^https?://' })
});
export class HttpAttachmentsResource {
    #httpClient;
    constructor(httpClient) {
        this.#httpClient = httpClient;
    }
    async getData(attachmentKey, options) {
        return await this.#download(attachmentKey, 'data', options);
    }
    async getEnail(attachmentKey, options) {
        return await this.#download(attachmentKey, 'enail', options);
    }
    async getThumbnail(attachmentKey, options) {
        return await this.#download(attachmentKey, 'thumbnail', options);
    }
    getUrl(attachmentKey) {
        return this.#createUrl(attachmentKey, 'data');
    }
    getEnailUrl(attachmentKey) {
        return this.#createUrl(attachmentKey, 'enail');
    }
    getThumbnailUrl(attachmentKey) {
        return this.#createUrl(attachmentKey, 'thumbnail');
    }
    async validateData(attachmentKey, options) {
        return await this.#validate(attachmentKey, 'data', options);
    }
    async validateEnail(attachmentKey, options) {
        return await this.#validate(attachmentKey, 'enail', options);
    }
    async validateThumbnail(attachmentKey, options) {
        return await this.#validate(attachmentKey, 'thumbnail', options);
    }
    async #download(attachmentKey, representation, options) {
        const payload = await this.#httpClient.get({
            path: '/attachment',
            queryParams: { ...this.#query(attachmentKey, representation), response: 'url' },
            payloadSchema: AttachmentDownloadUrlPayload,
            signal: options?.signal
        });
        try {
            new URL(payload.url);
        }
        catch (error) {
            throw new ApiResponseParseError('/attachment', error);
        }
        return await this.#httpClient.getBlob({
            url: payload.url,
            signal: options?.signal
        });
    }
    async #validate(attachmentKey, representation, options) {
        try {
            // Validate the application endpoint itself; an expiring storage redirect cannot
            // authoritatively classify the attachment as present or missing.
            const response = await this.#httpClient.head({
                path: '/attachment',
                queryParams: this.#query(attachmentKey, representation),
                signal: options?.signal
            });
            return this.#classifyExistence(response.status);
        }
        catch (error) {
            // Authorization and transient failures are not proof of deletion.
            return error instanceof ApiRequestError
                ? this.#classifyExistence(error.status)
                : 'indeterminate';
        }
    }
    #classifyExistence(status) {
        if (status === 404 || status === 410) {
            return 'missing';
        }
        if (status >= 200 && status < 300) {
            return 'exists';
        }
        return 'indeterminate';
    }
    #createUrl(attachmentKey, representation) {
        return this.#httpClient
            .createUrl('/attachment', this.#query(attachmentKey, representation))
            .toString();
    }
    #query(attachmentKey, representation) {
        return {
            key: attachmentKey,
            '404_status_on_fail': 'true',
            ...(representation === 'thumbnail' ? { thumbnail: 'true' } : {}),
            ...(representation === 'enail' ? { enail: 'true' } : {})
        };
    }
}
